import type { ClipPlayback, ClipPublic } from '@proref/types';
import type { TagClipInput, UploadUrlInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

export interface ListClipsParams {
  // Optional rule-article filter — the referee library's rule-nav bar narrows by this.
  ruleArticleId?: string;
  // Optional managed-tag filter — the referee library's tag chips narrow by this.
  tagId?: string;
  // Optional sport / federation filters — the super_admin clips console picks a sport, then
  // narrows to a federation. Compose with the actor's own visibility scope server-side.
  sportId?: string;
  federationId?: string;
  // Restrict to OFFICIAL (platform) clips — federation_id IS NULL. Used when authoring an Official
  // clip exam's question set.
  official?: boolean;
  // Restrict to PUBLISHED clips. The referee library passes this so the shelf shows exactly what a
  // referee sees — without it an admin (who may read their own federation's drafts) gets drafts
  // mixed into the library alongside live clips.
  published?: boolean;
  // Optional pagination — the referee library requests fixed-size pages for infinite scroll.
  limit?: number;
  offset?: number;
}

// The list endpoint returns the public projection — no answer columns. Federation scoping is
// applied server-side: a referee sees published clips in their own federation+sport only; a
// federation_admin also sees own-federation drafts; a super_admin spans all federations.
export async function listClips(params: ListClipsParams = {}): Promise<ClipPublic[]> {
  // Only send params that are set, so an all-clips call stays a clean `/clips` with no query.
  const query: Record<string, string> = {};
  if (params.ruleArticleId) query['rule_article_id'] = params.ruleArticleId;
  if (params.tagId) query['tag_id'] = params.tagId;
  if (params.sportId) query['sport_id'] = params.sportId;
  if (params.federationId) query['federation_id'] = params.federationId;
  if (params.official) query['official'] = 'true';
  if (params.published) query['published'] = 'true';
  // 0 is a valid offset, so guard on null/undefined rather than falsiness.
  if (params.limit != null) query['limit'] = String(params.limit);
  if (params.offset != null) query['offset'] = String(params.offset);
  const res = await api.get<Envelope<ClipPublic[]>>('/clips', {
    params: Object.keys(query).length > 0 ? query : undefined,
  });
  return unwrap(res.data);
}

// One clip with a freshly minted signed playback URL. The playback URL is null until the
// pipeline finishes (no hls_path yet) or when CloudFront isn't configured locally.
export async function getClip(id: string): Promise<ClipPlayback> {
  const res = await api.get<Envelope<ClipPlayback>>(`/clips/${id}`);
  return unwrap(res.data);
}

// Log a playback view — fired by the player on each watch (repeats included). Returns the
// fresh count so the UI can tick it up.
export async function recordView(clipId: string): Promise<{ views: number }> {
  const res = await api.post<Envelope<{ views: number }>>(`/clips/${clipId}/view`);
  return unwrap(res.data);
}

export interface UploadUrlResult {
  clip: ClipPublic;
  upload_url: string;
}

// Create the draft clip row + presigned PUT for the source MP4. The clip's id is the S3 path
// UUID — same identifier follows the file through MediaConvert + the webhook.
export async function createUploadUrl(input: UploadUrlInput): Promise<UploadUrlResult> {
  const res = await api.post<Envelope<UploadUrlResult>>('/media/upload-url', input);
  return unwrap(res.data);
}

// Tell the API the bytes have landed, so the clip leaves "Uploading" for the length of the
// transcode. The DB's only other writer between presign and completion is the MediaConvert
// webhook (which jumps straight to ready), so without this the whole transcode reads as an
// upload still in progress. Server-side it's a no-op if the webhook already got there first.
export async function markUploadComplete(clipId: string): Promise<void> {
  await api.post<Envelope<{ id: string; status: string }>>(`/media/uploads/${clipId}/complete`, {});
}

// PUT the file straight to S3 via the presigned URL. The signature has the content-type +
// exact content-length baked in, so S3 rejects a mismatched type/size with 403 — we don't
// re-validate here. fetch (not axios) keeps the request bare: no Authorization header would
// poison the presigned request, and we don't need axios interceptors on a one-off PUT.
export async function uploadToS3(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'video/mp4' },
    body: file,
  });
  if (!res.ok) {
    // Surface S3's error body (XML) verbatim so the operator can read it in DevTools, but
    // throw a clean message for the toast layer.
    const body = await res.text().catch(() => '');
    throw new Error(`S3 upload failed (HTTP ${res.status})${body ? ` — ${body.slice(0, 200)}` : ''}`);
  }
}

// Partial-update the clip — admin's "Tag the rule" + "Question & options" + "Commentary"
// step. The API accepts any subset of fields and leaves the rest untouched, so the editor
// can save piecemeal (e.g. just an option change) without rewriting the whole row.
export async function tagClip(id: string, input: TagClipInput): Promise<ClipPublic> {
  const res = await api.patch<Envelope<ClipPublic>>(`/clips/${id}`, input);
  return unwrap(res.data);
}

// Publish a draft → live. Gated server-side on status='ready' (HLS exists) AND on the three fields
// a referee needs in order to answer: rule_article_id + correct_decision + 2 or more options.
// decision_timestamp_ms is NOT required — a clip already cut to the moment needs no pause marker.
// The API responds 409 if still transcoding, or 400 naming the missing fields — the editor surfaces
// those via the toast/error envelope rather than pre-validating.
export async function publishClip(id: string): Promise<ClipPublic> {
  const res = await api.post<Envelope<ClipPublic>>(`/clips/${id}/publish`);
  return unwrap(res.data);
}

// Unpublish: live → draft. Mirror of publishClip — same endpoint, DELETE verb (the
// publish-state is a togglable resource). Idempotent server-side so a stale tab firing
// twice is a 200 no-op, not an error.
export async function unpublishClip(id: string): Promise<ClipPublic> {
  const res = await api.delete<Envelope<ClipPublic>>(`/clips/${id}/publish`);
  return unwrap(res.data);
}

// Pin this clip as the federation's daily clip for today — an admin override for the random
// select_daily_clip pick. Server-gated on the clip being published (409) and having a federation
// (400 — an official/platform clip can't be "today's clip" for anyone); idempotent, and pinning a
// second clip cleanly replaces any existing pin for the same federation+day rather than erroring.
export async function pinClip(id: string): Promise<ClipPublic> {
  const res = await api.post<Envelope<ClipPublic>>(`/clips/${id}/pin`);
  return unwrap(res.data);
}

// Unpin: mirror of pinClip, DELETE verb (same togglable-resource shape as publish/unpublish).
// Idempotent — already-unpinned is a 200 no-op.
export async function unpinClip(id: string): Promise<ClipPublic> {
  const res = await api.delete<Envelope<ClipPublic>>(`/clips/${id}/pin`);
  return unwrap(res.data);
}

// Hard delete — DB row + every S3 object under the clip's prefix. The API refuses a
// published clip with 409 / CLIP_PUBLISHED; the caller (kebab menu, editor footer)
// gates the UI to only invoke this on a draft so the 409 is a safety net, not a routine path.
export async function deleteClip(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/clips/${id}`);
}
